/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Avatar,
	Box,
	Text,
	Image,
	BackgroundImage,
} from '@mantine/core';

// Styles
import classes from './OrganizationProject.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import UserIcon from '@assets/icons/user-icon.svg';
import { getCountryName } from '@utils/countries';
import { Link } from '@components/atoms';
import { IconQuestionMark } from '@tabler/icons-react';
import BatimentLogo from '@assets/images/logo-batiment.svg';
import { NOT_APPLICABLE } from '@config/constants/common';
import {
	TOrganization,
	TProducer,
} from '@global-types/index';
import { ROLES } from '@config/constants/roles';
import clsx from 'clsx';

/* -------------------------------------------------------------------------- */
/*                                    Typs                                    */
/* -------------------------------------------------------------------------- */

type OrganizationProjectProps = {
	isPublic?: boolean;
	supportedBy?: string;
	contactProducer:
		| {
				firstName: string;
				lastName: string;
		  }
		| undefined;
	entity: TProducer;
	owner:
		| {
				locale: { language: string; timezone: string };
				firstName: string;
				lastName: string;
				email: string;
				role: string;
				createdAt: string;
				updatedAt: string;
				organization: string;
				id: string;
		  }
		| undefined;
	organization: TOrganization;
	cover: string;
	user:
		| {
				locale: { language: string; timezone: string };
				firstName: string;
				lastName: string;
				email: string;
				role: string;
				createdAt: string;
				updatedAt: string;
				organization: string;
				id: string;
		  }
		| undefined;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const OrganizationProject = (
	props: OrganizationProjectProps
) => {
	const {
		contactProducer,
		entity,
		owner,
		organization,
		cover,
		user,
		isPublic,
		supportedBy,
	} = props;
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.bloc1}>
			<Box
				className={clsx(
					classes.bloc1Image,
					isPublic && classes.bloc1ImagePublic
				)}
			>
				{/** Image */}
				<BackgroundImage
					src={cover}
					className={classes.image}
				>
					<Box className={classes.overlay} />
					<Box className={classes.containerAboveOverlay}>
						<img
							src={
								organization && organization?.logo
									? organization.logo
									: BatimentLogo
							}
							className={classes.logoOrganization}
						/>
						<Box
							className={
								classes.containerOrganizationInformations
							}
						>
							<Text
								className={classes.labelImageOrganization}
							>
								{t('global.organization')}
							</Text>
							<Text
								className={classes.valueImageOrganization}
							>
								{organization.name}
							</Text>
							{user && user.role === ROLES.USER && (
								<Box
									className={
										classes.groupCountryInformations
									}
									data-testid="bloc-informations"
								>
									<Avatar
										className={classes.logoCountry}
										radius={'xl'}
										src={`https://flagcdn.com/w40/${organization.location && organization.location.countryCode ? organization.location.countryCode.toLowerCase() : ''}.png`}
										size={'xs'}
									>
										<IconQuestionMark />
									</Avatar>

									<Box
										className={
											classes.groupCountryTextInformations
										}
									>
										<Text
											className={
												classes.textCountryPublic
											}
										>
											{getCountryName(
												organization.location.countryCode
													? organization.location
															.countryCode
													: ''
											)}
										</Text>
										<Text
											className={
												classes.textCountryPlacePublic
											}
										>
											{`/ ${organization.location && organization.location.place ? organization.location.place : NOT_APPLICABLE}`}
										</Text>
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				</BackgroundImage>
			</Box>
			{user && user.role !== ROLES.USER && (
				<Box
					className={clsx(
						isPublic
							? classes.containerBloc1InformationsPublic
							: classes.containerBloc1Informations
					)}
				>
					{/*Organization */}
					{!isPublic && (
						<Box className={classes.bloc1Informations}>
							{/* Location */}
							<Box className={classes.groupLabelValue}>
								<Text
									className={classes.labelOrganization}
								>
									{t('projectDetails.location')}
								</Text>
								<Box
									className={
										classes.groupCountryInformations
									}
								>
									<Avatar
										className={classes.logoCountry}
										radius={'xl'}
										src={`https://flagcdn.com/w40/${organization.location && organization.location.countryCode ? organization.location.countryCode.toLowerCase() : ''}.png`}
										size={'xs'}
									>
										<IconQuestionMark />
									</Avatar>

									<Box
										className={
											classes.groupCountryTextInformations
										}
									>
										<Text className={classes.textCountry}>
											{getCountryName(
												organization.location.countryCode
													? organization.location
															.countryCode
													: ''
											)}
										</Text>
										<Text
											className={classes.textCountryPlace}
										>
											{`/ ${organization.location && organization.location.place ? organization.location.place : NOT_APPLICABLE}`}
										</Text>
									</Box>
								</Box>
							</Box>
							{/* Owner */}
							<Box className={classes.groupLabelValue}>
								<Text
									className={classes.labelOrganization}
								>
									{t('projectDetails.owner')}
								</Text>
								<Box
									className={
										classes.groupUserInformations
									}
								>
									<Image fit="contain" src={UserIcon} />
									<Text
										className={classes.valueOrganization}
									>
										{owner
											? `${owner.firstName} ${owner.lastName}`
											: NOT_APPLICABLE}
									</Text>
									<Link
										disabled
										size="sm"
										underline="always"
									>
										{t('global.seeDetails')}
									</Link>
								</Box>
							</Box>
							{/* Identifiant legal */}
							<Box className={classes.groupLabelValue}>
								<Text
									className={classes.labelOrganization}
								>
									{t(
										'projectDetails.organizationLegalIdentifier'
									)}
								</Text>
								<Text
									className={classes.valueOrganization}
								>
									{organization.legalIdentifier
										? organization.legalIdentifier
										: NOT_APPLICABLE}
								</Text>
							</Box>
						</Box>
					)}
					{/*Producer */}
					<Box className={classes.bloc1Informations}>
						{/* Producer name */}
						<Box className={classes.groupLabelValue}>
							<Text className={classes.labelOrganization}>
								{t('projectDetails.producerName')}
							</Text>
							<Text className={classes.valueOrganization}>
								{entity.name
									? entity.name
									: NOT_APPLICABLE}
							</Text>
						</Box>
						{/* Producer location  */}
						<Box className={classes.groupLabelValue}>
							<Text className={classes.labelOrganization}>
								{t('projectDetails.producerLocation')}
							</Text>
							<Box
								className={
									classes.groupCountryInformations
								}
							>
								<Avatar
									className={classes.logoCountry}
									radius={'xl'}
									src={`https://flagcdn.com/w40/${entity.location && entity.location.countryCode ? entity.location.countryCode.toLowerCase() : ''}.png`}
									size={'xs'}
								>
									<IconQuestionMark />
								</Avatar>

								<Box
									className={
										classes.groupCountryTextInformations
									}
								>
									<Text className={classes.textCountry}>
										{getCountryName(
											entity.location &&
												entity.location.countryCode
												? entity.location.countryCode
												: ''
										)}
									</Text>
									<Text
										className={classes.textCountryPlace}
									>
										{`/ ${entity.location && entity.location.place ? entity.location.place : NOT_APPLICABLE}`}
									</Text>
								</Box>
							</Box>
						</Box>
						{/* Producer legal identifier  */}
						{!isPublic && (
							<Box className={classes.groupLabelValue}>
								<Text
									className={classes.labelOrganization}
								>
									{t(
										'projectDetails.producerLegalIdentifier'
									)}
								</Text>
								<Text
									className={classes.valueOrganization}
								>
									{entity && entity.legalIdentifier
										? entity.legalIdentifier
										: NOT_APPLICABLE}
								</Text>
							</Box>
						)}
						{/* Owner */}
						{!isPublic && (
							<Box className={classes.groupLabelValue}>
								<Text
									className={classes.labelOrganization}
								>
									{t('projectDetails.producerContact')}
								</Text>
								<Box
									className={
										classes.groupUserInformations
									}
								>
									<Image fit="contain" src={UserIcon} />
									<Text
										data-testid="contact-producer-text"
										className={classes.valueOrganization}
									>
										{contactProducer
											? `${contactProducer.firstName} ${contactProducer.lastName}`
											: NOT_APPLICABLE}
									</Text>
									<Link
										disabled
										size="sm"
										underline="always"
									>
										{t('global.seeDetails')}
									</Link>
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};
